import React, {useCallback, useMemo, useState} from "react"
import {useSelector} from "react-redux"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {ColumnModel, SettingsList} from "../../../../components/Settings/SettingsList"
import {useParams} from "react-router-dom"
import {useSetSelector} from "../../../../hooks/set-selector"
import {useFilterSelector} from "../../../../hooks/filter-selector"
import {FrameUIEntity} from "../../../../store/control/state"
import {useControlSelector} from "../../../../hooks/control-selector"
import {useLocalization} from "../../../../hooks/localization"
import {TFunction} from "i18next"

const BACK_URL = "/settings/frame-editor"
const getColumns = (bridgeDeviceTypeId: string, t: TFunction): ColumnModel[] => [
    {
        length: 3,
        name: t("display_name"),
        key: "name"
    },
    {
        length: 3,
        name: t("type"),
        key: "type"
    },
    {
        length: 1,
        name: t("page"),
        key: "page_number"
    },
    {
        length: 2,
        name: t("animation"),
        key: "is_animation_active"
    },
    {
        length: 1,
        name: t("icon_color"),
        key: "icon_color_type"
    }
]

const keyExtractor = (item: FrameUIEntity) => item.id

export function FrameEditorContentListScreen() {
    const {t} = useLocalization()
    const params = useParams<{id: string}>()

    const devices = useSelector(state => state.control.bridgeDeviceList)
    const deviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList)
    const selectedDevice = useMemo(() => devices.find(d => d.id === params.id), [devices, params.id])
    const selectedDeviceType = useMemo(() => deviceTypeSet[selectedDevice.type_id], [deviceTypeSet, selectedDevice])

    const stpUIEntityList = useFilterSelector(
        state => state.control.frameUIList,
        e => e.bridge_device_id === selectedDevice.id
    )
    const [selectedUiEntity, setSelectedUiEntity] = useState<FrameUIEntity>()
    const controlTypeSet = useSetSelector(state => state.control.controlTypeList)
    const controlSet = useSetSelector(state => state.control.controlList)
    const virtualControlSet = useSetSelector(state => state.control.virtualControlList)
    const controlList = useControlSelector()

    const displayNameExtractor = useCallback(
        (item: FrameUIEntity, column: ColumnModel) => {
            if (column?.key === "type") {
                switch (item.type) {
                    case "weather":
                        return t("weather")
                    case "scenario":
                        return t("scenario")
                    case "control":
                    case "virtualControl":
                        const control = controlList.find(c => c.id === item.relational_id)
                        return controlTypeSet[control?.control.type_id]?.name || t("control")
                    default:
                        return "-"
                }
            }

            if (column?.key === "name") {
                if (item.type === "weather") {
                    return t("weather") || "-"
                }
                if (item.type === "control") {
                    return controlSet[item?.relational_id]?.name || "-"
                }
                if (item.type === "virtualControl") {
                    const isRemoteControl = virtualControlSet[item?.relational_id]?.parameters?.["is_remote_control"]
                        ? virtualControlSet[item?.relational_id]?.parameters?.["remote_control_name"]
                        : controlSet[virtualControlSet[item?.relational_id]?.control_id]?.name
                    return isRemoteControl || "-"
                }
                return item?.extras?.name || "-"
            }

            if (column?.key === "is_animation_active") {
                return item.is_animation_active ? t("active") : t("passive") || "-"
            }

            if (column?.key === "page_number") {
                return item.page_number || "-"
            }

            if (column?.key === "icon_color_type") {
                if (item.icon_color_type === "0") {
                    return t("white")
                } else if (item.icon_color_type === "1") {
                    return t("black")
                } else if (item.icon_color_type === "2") {
                    return t("gold")
                } else if (item.icon_color_type === "3") {
                    return t("silver")
                } else if (item.icon_color_type === "4") {
                    return t("rose")
                } else {
                    return "-"
                }
            }

            return item[column?.key] || "-"
        },
        [t, controlList, controlTypeSet, controlSet, virtualControlSet]
    )

    const buttons = useMemo(
        () => [
            {
                text: t("edit"),
                url: `/settings/frame-editor/${selectedDevice?.id}/content/${selectedUiEntity?.id}`,
                className: "blue",
                hidden: !selectedUiEntity
            },
            {
                text: t("add"),
                url: `/settings/frame-editor/${selectedDevice?.id}/content`,
                className: "green"
            }
        ],
        [t, selectedDevice.id, selectedUiEntity]
    )

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer className="service-edit" buttons={buttons} backUrl={BACK_URL} searchable={true}>
                <SettingsList
                    columns={getColumns(selectedDeviceType.id, t)}
                    items={stpUIEntityList}
                    selectedItem={selectedUiEntity}
                    onItemSelected={setSelectedUiEntity}
                    displayNameExtractor={displayNameExtractor}
                    keyExtractor={keyExtractor}
                />
            </SettingsPageContainer>
        </PasswordContainer>
    )
}
